import { Fetcher, trackClick } from '@this/src/util';
import React, { useState, useCallback, useMemo, useContext } from 'react';
import _ from 'lodash';
import Text from '@this/src/components/shared/text/text';

import type { PaymentGatewayType, PaymentMethodType } from '@this/domain/organization/organization3';
import { reportError } from '@this/lib/bugsnag';
import type { PaymentGatewayMember } from '@this/domain/reserve_info2';
import type ReserveInfo from '@this/domain/reserve_info2';
import { createAITTheme } from '@this/src/components/shared/ui/theme';
import { useAITTheme } from '@this/src/components/shared/ui/theme/provider';

interface CardsResponse {
  paymentGatewayMembers: PaymentGatewayMember[];
}

interface Props {
  reserveInfo: ReserveInfo;
}

const PaymentSection: React.FC<Props> = ({ reserveInfo }) => {
  const [deleteCardStatus, setDeleteCardStatus] = useState<'none' | 'deleting' | 'success' | 'fail'>('none');
  const [cardCompanyImageNum, setCardCompanyImageNum] = useState<'' | '01' | '02' | '03' | '04' | '05' | '06'>('');
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const fetchCards = useCallback(() => {
    Fetcher.get<CardsResponse>('/payment/cards').then(
      result => {
        const { paymentGatewayMembers } = result;
        reserveInfo.paymentGatewayMembers = paymentGatewayMembers;
      },
      _e => {
        // updateCardsは渡されない
        /* this.props.updateCards([]); */
      }
    );
  }, [reserveInfo]);

  const judgeIssuingCompany = useCallback(() => {
    let imageNum: '' | '01' | '02' | '03' | '04' | '05' | '06' = '';
    const { cardNum01 } = reserveInfo;
    const { cardNum02 } = reserveInfo;
    if (cardNum01.length !== 0 && (cardNum02.length === 0 || cardNum01.length === 4)) {
      const companyNumStr = cardNum01 + cardNum02.substring(0, 2);
      const companyNum = Number(companyNumStr);
      const companyNum1 = Number(companyNumStr[0]);
      const companyNum2 = Number(companyNumStr.substr(0, 2));
      const companyNum3 = Number(companyNumStr.substr(0, 3));
      const companyNum4 = Number(companyNumStr.substr(0, 4));
      const companyNum5 = Number(companyNumStr.substr(0, 5));
      if (companyNum1 === 4) {
        imageNum = '01';
      } else if (companyNum1 === 5) {
        imageNum = '02';
      } else if ([34, 37].indexOf(companyNum2) >= 0) {
        imageNum = '03';
      } else if (companyNum4 >= 3528 && companyNum4 <= 3589) {
        imageNum = '04';
      } else if (
        [36, 38, 39].indexOf(companyNum2) >= 0 ||
        companyNum4 === 3095 ||
        (companyNum3 >= 300 && companyNum3 <= 305)
      ) {
        imageNum = '05';
      } else if (
        companyNum5 === 60110 ||
        companyNum2 === 65 ||
        (companyNum5 >= 60112 && companyNum5 <= 60114) ||
        (companyNum * 1 >= 601174 && companyNum * 1 <= 601179) ||
        (companyNum * 1 >= 601186 && companyNum * 1 <= 601199) ||
        (companyNum3 >= 644 && companyNum3 <= 649)
      ) {
        imageNum = '06';
      }
    }
    if (cardCompanyImageNum !== imageNum) {
      setCardCompanyImageNum(imageNum);
    }
  }, [reserveInfo, cardCompanyImageNum]);

  const handleAutoNextFocus = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      if (target.id.indexOf('card_number') >= 0) {
        judgeIssuingCompany();
      }
      if (e.keyCode < 48 || e.keyCode > 57) {
        return;
      }
      if (target.value && target.value.length === Number($(e.target).attr('maxLength'))) {
        $(`#${$(e.target).data('nextelementid')}`).focus();
      }
    },
    [judgeIssuingCompany]
  );

  const handleDeleteCard = useCallback(
    (payment_gateway_type: PaymentGatewayType | undefined, payment_gateway_uid: string) =>
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        trackClick('Delete card');
        e.preventDefault();
        setDeleteCardStatus('deleting');
        Fetcher.delete(`/payment/cards/${payment_gateway_uid}.json`, { payment_gateway_type }).then(
          _result => {
            fetchCards();
            setDeleteCardStatus('success');
          },
          _error => {
            setDeleteCardStatus('fail');
          }
        );
      },
    [fetchCards]
  );

  const handlePaymentTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedPaymentMethodType = e.target.value as PaymentMethodType;
      reserveInfo.paymentMethod = selectedPaymentMethodType;
      if (
        selectedPaymentMethodType === 'card' &&
        reserveInfo.paymentGatewayMembers &&
        reserveInfo.paymentGatewayMembers.length > 0
      ) {
        reserveInfo.paymentTransaction = { uid: reserveInfo.paymentGatewayMembers[0].uid };
      }
    },
    [reserveInfo]
  );

  const handlePaymentTransaction = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      reserveInfo.paymentTransaction = { uid: e.target.value };
    },
    [reserveInfo]
  );

  try {
    return (
      <StylesContext.Provider value={styles}>
        <div data-testid="reserve-confirm-input-payment-root">
          <PaymentFlash>
            {deleteCardStatus === 'success' ? (
              <div className="flash succeed" data-testid="reserve-confirm-input-payment-delete-success">
                クレジットカードのご登録情報を削除しました。
              </div>
            ) : deleteCardStatus === 'fail' ? (
              <div className="flash faild" data-testid="reserve-confirm-input-payment-delete-fail">
                通信環境が不安定です。
                <br />
                時間をおいてもう一度お試しください。
              </div>
            ) : null}
          </PaymentFlash>
          <form /* onSubmit={openPaymentConfirm} */>
            <PaymentFormSubArea>
              {deleteCardStatus === 'deleting' ? (
                <PaymentDeletingFlash data-testid="reserve-confirm-input-payment-deleting-flash">
                  処理中...
                </PaymentDeletingFlash>
              ) : (
                <>
                  {(reserveInfo.paymentMethodType === 'selectable' ||
                    reserveInfo.paymentMethodType === 'invoice') &&
                    reserveInfo.user?.organization && (
                      <PaymentCards data-testid="reserve-confirm-input-payment-method-billing-radio">
                        <input
                          data-testid="reserve-confirm-input-payment-method-billing-radio-input"
                          type="radio"
                          value="billing"
                          checked={reserveInfo.paymentMethod === 'billing'}
                          onChange={handlePaymentTypeChange}
                        />
                        <span data-testid="reserve-confirm-input-payment-method-billing-label">
                          {reserveInfo.user.organization.name}に請求する
                        </span>
                      </PaymentCards>
                    )}
                  {(reserveInfo.paymentMethodType === 'selectable' ||
                    reserveInfo.paymentMethodType === 'card') && (
                    <>
                      <PaymentCards id="card" data-testid="reserve-confirm-input-payment-method-card-radio">
                        <input
                          data-testid="reserve-confirm-input-payment-method-card-radio-input"
                          type="radio"
                          value="card"
                          checked={reserveInfo.paymentMethod === 'card'}
                          onChange={handlePaymentTypeChange}
                        />
                        <span data-testid="reserve-confirm-input-payment-method-card-label">
                          クレジットカードで決済する
                        </span>
                      </PaymentCards>
                      {reserveInfo.paymentMethod === 'card' && (
                        <div className="payment__card-area" data-testid="reserve-confirm-input-payment-card-area">
                          {reserveInfo.paymentGatewayType === 'sony' && (
                            <p data-testid="reserve-confirm-input-payment-registered-card-note">
                              {reserveInfo.paymentGatewayMembers && reserveInfo.paymentGatewayMembers.length > 0
                                ? '事前に登録されたクレジットカードで決済'
                                : '支払い用のクレジットカードが登録されていません。管理者の方にご確認ください。'}
                            </p>
                          )}
                          {reserveInfo.paymentGatewayType === 'gmo' &&
                            reserveInfo.paymentGatewayMembers &&
                            reserveInfo.paymentGatewayMembers.length > 0 &&
                            reserveInfo.paymentGatewayMembers.map((member, i) => (
                              <PaymentCards
                                key={i}
                                data-testid={`reserve-confirm-input-payment-member-option-${i}`}
                              >
                                <input
                                  data-testid={`reserve-confirm-input-payment-member-radio-${i}`}
                                  type="radio"
                                  value={member.uid}
                                  checked={reserveInfo.paymentTransaction?.uid === member.uid}
                                  onChange={handlePaymentTransaction}
                                />
                                <span data-testid={`reserve-confirm-input-payment-member-label-${i}`}>
                                  {member.name}
                                </span>
                                <PaymentDelCard
                                  href="#"
                                  onClick={handleDeleteCard(reserveInfo.paymentGatewayType, member.uid)}
                                >
                                  このカードを削除
                                </PaymentDelCard>
                              </PaymentCards>
                            ))}
                          {reserveInfo.paymentGatewayType === 'gmo' && (
                            <PaymentCardsNew
                              id="payment-card-new-label"
                              data-testid="reserve-confirm-input-payment-new-card-radio"
                            >
                              <input
                                data-testid="reserve-confirm-input-payment-new-card-radio-input"
                                type="radio"
                                value="new"
                                checked={reserveInfo.paymentTransaction?.uid === 'new'}
                                onChange={handlePaymentTransaction}
                              />
                              <span>新しいカードで決済</span>
                            </PaymentCardsNew>
                          )}
                          {reserveInfo.paymentGatewayType === 'gmo' &&
                            reserveInfo.paymentTransaction?.uid === 'new' && (
                              <>
                                <PaymentNewCardArea data-testid="reserve-confirm-input-payment-gmo-new-card-form">
                                  <PaymentSectionComponent>
                                    <PaymentLeftLabel htmlFor="card_number">
                                      カード番号
                                      {cardCompanyImageNum !== '' && (
                                        <PaymentLeftLabelImgWrap>
                                          <PaymentLeftLabelImg
                                            src={`/images/logo_card${cardCompanyImageNum}.gif`}
                                          />
                                        </PaymentLeftLabelImgWrap>
                                      )}
                                    </PaymentLeftLabel>
                                    <PaymentSectionRight>
                                      <PaymentInputCardNumber
                                        type="number"
                                        pattern="\\d*"
                                        name="card[number_1]"
                                        id="card_number_1"
                                        placeholder="0000"
                                        value={reserveInfo.cardNum01}
                                        maxLength={4}
                                        onChange={e => {
                                          reserveInfo.cardNum01 = e.target.value;
                                        }}
                                        onKeyUp={handleAutoNextFocus}
                                        data-nextelementid="card_number_2"
                                      />
                                      <span>-</span>
                                      <PaymentInputCardNumber
                                        type="number"
                                        pattern="\\d*"
                                        name="card[number_2]"
                                        id="card_number_2"
                                        placeholder="0000"
                                        value={reserveInfo.cardNum02}
                                        maxLength={4}
                                        onChange={e => {
                                          reserveInfo.cardNum02 = e.target.value;
                                        }}
                                        onKeyUp={handleAutoNextFocus}
                                        data-nextelementid="card_number_3"
                                      />
                                      <span>-</span>
                                      <PaymentInputCardNumber
                                        type="number"
                                        pattern="\\d*"
                                        name="card[number_3]"
                                        id="card_number_3"
                                        placeholder="0000"
                                        value={reserveInfo.cardNum03}
                                        maxLength={4}
                                        onChange={e => {
                                          reserveInfo.cardNum03 = e.target.value;
                                        }}
                                        onKeyUp={handleAutoNextFocus}
                                        data-nextelementid="card_number_4"
                                      />
                                      <span>-</span>
                                      <PaymentInputCardNumber
                                        type="number"
                                        pattern="\\d*"
                                        name="card[number_4]"
                                        id="card_number_4"
                                        placeholder="0000"
                                        value={reserveInfo.cardNum04}
                                        maxLength={4}
                                        onChange={e => {
                                          reserveInfo.cardNum04 = e.target.value;
                                        }}
                                        onKeyUp={handleAutoNextFocus}
                                        data-nextelementid="card_expire_month"
                                      />
                                    </PaymentSectionRight>
                                  </PaymentSectionComponent>
                                  <PaymentSectionComponent>
                                    <PaymentLeftLabel htmlFor="card_expire_month">有効期限</PaymentLeftLabel>
                                    <PaymentSectionRight>
                                      <PaymentExpire
                                        type="number"
                                        pattern="\\d*"
                                        name="card[expire_month]"
                                        id="card_expire_month"
                                        placeholder="MM"
                                        value={reserveInfo.cardExpireMonth}
                                        maxLength={2}
                                        onChange={e => {
                                          reserveInfo.cardExpireMonth = e.target.value;
                                        }}
                                        onKeyUp={handleAutoNextFocus}
                                        data-nextelementid="card_expire_year"
                                      />
                                      <PaymentSlash> / </PaymentSlash>
                                      <PaymentExpire
                                        type="number"
                                        pattern="\\d*"
                                        name="card[expire_year]"
                                        id="card_expire_year"
                                        placeholder="YY"
                                        value={reserveInfo.cardExpireYear}
                                        maxLength={2}
                                        onChange={e => {
                                          reserveInfo.cardExpireYear = e.target.value;
                                        }}
                                        onKeyUp={handleAutoNextFocus}
                                        data-nextelementid="card_name"
                                      />
                                    </PaymentSectionRight>
                                  </PaymentSectionComponent>
                                  <PaymentSectionComponent>
                                    <PaymentLeftLabel htmlFor="card_name">カード名義</PaymentLeftLabel>
                                    <PaymentSectionRight>
                                      <PaymentInput
                                        type="text"
                                        name="card[name]"
                                        id="card_name"
                                        placeholder="ICHIRO YAMADA"
                                        value={reserveInfo.cardName}
                                        onChange={e => {
                                          reserveInfo.cardName = e.target.value;
                                        }}
                                      />
                                    </PaymentSectionRight>
                                  </PaymentSectionComponent>
                                  <PaymentSectionComponent>
                                    <PaymentLeftLabel htmlFor="card_code">セキュリティコード</PaymentLeftLabel>
                                    <PaymentSectionRight>
                                      <PaymentInput
                                        type="text"
                                        name="card[code]"
                                        id="card_code"
                                        placeholder="000"
                                        value={reserveInfo.cardCode}
                                        onChange={e => {
                                          reserveInfo.cardCode = e.target.value;
                                        }}
                                      />
                                    </PaymentSectionRight>
                                  </PaymentSectionComponent>
                                </PaymentNewCardArea>
                                {!_.isEmpty(reserveInfo.validationErrors.card) && (
                                  <ReserveConfirmErrors>
                                    <Error>
                                      <Text text={reserveInfo.validationErrors.card} />
                                    </Error>
                                  </ReserveConfirmErrors>
                                )}
                              </>
                            )}
                          <p data-testid="reserve-confirm-input-payment-note">※ 予約時に決済となります。</p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </PaymentFormSubArea>
          </form>
        </div>
      </StylesContext.Provider>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const StylesContext = React.createContext<ReturnType<typeof getStyles> | null>(null);
const fallbackStyles = getStyles(createAITTheme());
const useStylesContext = () => useContext(StylesContext) ?? fallbackStyles;

const PaymentFlash: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentFlash, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentFormSubArea: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentFormSubArea, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentDeletingFlash: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentDeletingFlash, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentCards: React.FC<React.ComponentProps<'label'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.paymentCards, ...props.style }}>
      {children}
    </label>
  );
};

const PaymentCardsNew: React.FC<React.ComponentProps<'label'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.paymentCardsNew, ...props.style }}>
      {children}
    </label>
  );
};

const PaymentDelCard: React.FC<React.ComponentProps<'a'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <a {...props} style={{ ...styles.paymentDelCard, ...props.style }}>
      {children}
    </a>
  );
};

const PaymentNewCardArea: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentNewCardArea, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentSectionComponent: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentSectionComponent, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentSectionRight: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentSectionRight, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentLeftLabel: React.FC<React.ComponentProps<'label'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <label {...props} style={{ ...styles.paymentLeftLabel, ...props.style }}>
      {children}
    </label>
  );
};

const PaymentLeftLabelImgWrap: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentLeftLabelImgWrap, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentLeftLabelImg: React.FC<React.ComponentProps<'img'>> = props => {
  const styles = useStylesContext();
  return <img {...props} style={{ ...styles.paymentLeftLabelImg, ...props.style }} />;
};

const PaymentInput: React.FC<React.ComponentProps<'input'>> = props => {
  const styles = useStylesContext();
  return <input {...props} style={{ ...styles.paymentInput, ...props.style }} />;
};

const PaymentInputCardNumber: React.FC<React.ComponentProps<'input'>> = props => {
  const styles = useStylesContext();
  return <input {...props} style={{ ...styles.paymentInputCardNumber, ...props.style }} />;
};

const PaymentSlash: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.paymentSlash, ...props.style }}>
      {children}
    </div>
  );
};

const PaymentExpire: React.FC<React.ComponentProps<'input'>> = props => {
  const styles = useStylesContext();
  return <input {...props} style={{ ...styles.paymentExpire, ...props.style }} />;
};

const ReserveConfirmErrors: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.reserveConfirmErrors, ...props.style }}>
      {children}
    </div>
  );
};

const Error: React.FC<React.ComponentProps<'div'>> = ({ children, ...props }) => {
  const styles = useStylesContext();
  return (
    <div {...props} style={{ ...styles.error, ...props.style }}>
      {children}
    </div>
  );
};

function getStyles(theme: ReturnType<typeof useAITTheme>['theme']) {
  const labelWidth = '130px';
  return {
    paymentFlash: {
      margin: '0 0 10px'
    },
    paymentFormSubArea: {
      marginTop: 15
    },
    paymentDeletingFlash: {
      margin: '10px 0'
    },
    paymentCards: {
      overflow: 'hidden',
      marginBottom: 15
    },
    paymentCardsNew: {
      marginTop: 15
    },
    paymentDelCard: {
      display: 'inline-block',
      float: 'right' as const
    },
    paymentNewCardArea: {
      marginLeft: 20
    },
    paymentSectionComponent: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      padding: '5px 0',
      lineHeight: '27px'
    },
    paymentSectionRight: {
      display: 'flex',
      width: 300
    },
    paymentLeftLabel: {
      fontWeight: 'normal' as const,
      width: labelWidth
    },
    paymentLeftLabelImgWrap: {
      display: 'inline-block',
      width: 36,
      height: 23,
      margin: '0 0 0 8px',
      verticalAlign: 'middle' as const
    },
    paymentLeftLabelImg: {
      display: 'block',
      maxHeight: '100%'
    },
    paymentInput: {
      width: `calc(100% - ${labelWidth})`
    },
    paymentInputCardNumber: {
      width: '4em'
    },
    paymentSlash: {
      width: 20,
      textAlign: 'center' as const
    },
    paymentExpire: {
      width: '3em'
    },
    reserveConfirmErrors: {
      marginBottom: 20
    },
    error: {
      color: theme.tokens.colors.danger.primary
    }
  };
}

export default PaymentSection;
